import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AuthService, UserService } from '@sp-core-services';
import { PermissionsService } from '@sp-core/services/permissions/permissions.service';
import { DisclaimerService } from '@sp-core/services/disclaimer/disclaimer.service';

type DenialReason = 'no-user' | 'no-permissions' | 'permission-denied';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private router: Router,
    private userService: UserService,
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private disclaimerService: DisclaimerService,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<UrlTree | boolean> {
    return combineLatest([
      this.userService.user$,
      this.permissionsService.permissions$,
      this.permissionsService.role$,
    ]).pipe(
      take(1),
      switchMap(([existUser, currentPermissions, currentRole]) => {
        if (!existUser) {
          this.recordDenial(route, currentRole, 'no-user');
          this.authService.clearData();
          return of(false);
        }
        if (!currentPermissions) {
          this.recordDenial(route, currentRole, 'no-permissions');
          return of(this.redirectToRoleSelection());
        }
        const { permissions } = route.data;
        if (!permissions.some((permission) => currentPermissions.has(permission))) {
          this.recordDenial(route, currentRole, 'permission-denied');
          return of(this.redirectToHomePage(currentRole, state.url));
        }
        return this.disclaimerService.ensureAccepted(currentRole);
      }),
    );
  }

  // Signed in, but no role chosen yet (the role cookie is missing, or loading the role's
  // permissions failed): the role selection screen is the way out, not a second password and 2FA.
  // It works only on the first navigation of a page load: inside a running app SignInComponent
  // clears the user as soon as it opens, so the session is ended here instead, as before, rather
  // than left valid behind a sign-in form.
  private redirectToRoleSelection(): UrlTree | boolean {
    if (this.router.navigated) {
      this.authService.clearData();
      return false;
    }
    return this.router.parseUrl('/sign-in');
  }

  // A page of another role, usually an old bookmark: open the user's own home page instead of
  // logging them out. A role denied its own home page goes to the public contact page, so the
  // redirect cannot loop.
  private redirectToHomePage(role: IRole, deniedUrl: string): UrlTree {
    const homeUrl = this.authService.getHomeUrl(role);
    return this.router.parseUrl(deniedUrl.startsWith(homeUrl) ? '/contact-us' : homeUrl);
  }

  // Every denial is handled above, so none of them is a Sentry event. The breadcrumb keeps the
  // denial in the trail of the next real event of this page session (e.g. "Role switch failed").
  private recordDenial(route: ActivatedRouteSnapshot, role: IRole, reason: DenialReason): void {
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'info',
      message: 'Route access denied',
      data: {
        reason,
        path: route.routeConfig?.path ?? null,
        roleKey: role?.key ?? null,
        requiredPermissions: route.data?.permissions ?? null,
      },
    });
  }
}
